'use client';

import { useTranslations, useLocale } from 'next-intl';
import { UserPlus, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Link } from '@/i18n/routing';
import { PermissionGate } from '@/features/auth';
import { RoleColorBadge } from '@/features/roles';
import type { User } from '../types';
import { isSuperAdminUser } from '../utils';

interface UserCardProps {
  user: User;
  onAssignRole: (user: User) => void;
  onToggleStatus: (user: User) => void;
}

function getRoleDisplayName(
  role: { name: string; name_en: string | null; name_ar: string | null },
  locale: string
): string {
  if (locale === 'ar' && role.name_ar) return role.name_ar;
  if (locale === 'en' && role.name_en) return role.name_en;
  return role.name_en || role.name_ar || role.name;
}

export function UserCard({
  user,
  onAssignRole,
  onToggleStatus
}: UserCardProps) {
  const t = useTranslations('users');
  const locale = useLocale();

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const isDisabled = isSuperAdminUser(user) || user.status === 'locked';

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        <div className='mb-4 flex items-start justify-between'>
          <div className='flex items-center gap-3'>
            <div className='bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold'>
              {initials}
            </div>
            <div>
              <h3 className='font-semibold'>{user.name}</h3>
              <p className='text-muted-foreground text-sm'>{user.email}</p>
            </div>
          </div>
          <Switch
            checked={user.status === 'active'}
            disabled={isDisabled}
            onCheckedChange={() => onToggleStatus(user)}
          />
        </div>

        <div className='mb-3 flex flex-wrap gap-1'>
          {user.roles.length > 0 ? (
            user.roles.map((role) => (
              <RoleColorBadge
                key={role.name}
                name={getRoleDisplayName(role, locale)}
                color={role.color}
              />
            ))
          ) : (
            <span className='text-muted-foreground text-sm'>
              {t('noRolesAssigned')}
            </span>
          )}
        </div>

        {(user.office || user.workplace) && (
          <div className='text-muted-foreground mb-4 text-sm'>
            {user.office && <span>{user.office}</span>}
            {user.office && user.workplace && <span> · </span>}
            {user.workplace && <span>{user.workplace}</span>}
          </div>
        )}

        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          {!isSuperAdminUser(user) && (
            <PermissionGate permissions={['roles.edit']}>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8'
                onClick={() => onAssignRole(user)}
              >
                <UserPlus className='h-4 w-4' />
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permissions={['users.edit']}>
            <Button variant='ghost' size='icon' className='h-8 w-8' asChild>
              <Link href={`/dashboard/users/${user.id}/edit`}>
                <Pencil className='h-4 w-4' />
              </Link>
            </Button>
          </PermissionGate>
        </div>
      </CardContent>
    </Card>
  );
}
